'use client';

import { motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import Image from 'next/image';
import { useState } from 'react';

import { Prize } from '@/lib/prize-wheel/prizes';
import { richTextComponent } from '@/sanity/sanityPortableText';
import classicalBg from '@/public/prize-wheel/hero-classical-bg.png';

import PrizeResultDialog, { PrizeResultCopy } from './PrizeResultDialog';
import PrizeWheel from './PrizeWheel';

import type { PortableTextBlock } from 'sanity';

const ease = [0.25, 0.1, 0.25, 1] as const;

export interface PrizeWheelHeroData {
  heroChip?: string;
  heroTitle?: PortableTextBlock[];
  heroTitleMobile?: PortableTextBlock[];
  spinLabel?: string;
  modalEyebrow?: string;
  modalTopPrizeEyebrow?: string;
  modalBodyChallenge?: string;
  modalBodyFunded?: string;
  modalBodyVoucher?: string;
  modalCtaLabel?: string;
}

export default function PrizeWheelHeroSection({
  data,
}: {
  data: PrizeWheelHeroData;
}) {
  const [result, setResult] = useState<Prize | null>(null);

  const modalCopy: PrizeResultCopy = {
    eyebrow: data.modalEyebrow,
    topPrizeEyebrow: data.modalTopPrizeEyebrow,
    bodyChallenge: data.modalBodyChallenge,
    bodyFunded: data.modalBodyFunded,
    bodyVoucher: data.modalBodyVoucher,
    ctaLabel: data.modalCtaLabel,
  };

  return (
    <section className="relative flex w-full flex-col items-center overflow-hidden bg-white font-geist max-lg:px-[2rem] max-lg:pb-[5.25rem] max-lg:pt-[6rem] lg:px-[6rem] lg:pb-[1.625rem] lg:pt-[9rem]">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 flex justify-center max-lg:top-0 lg:-top-[8.375rem]"
      >
        {(['left', 'centre', 'right'] as const).map((tile) => (
          <div
            key={tile}
            className={`relative shrink-0 max-lg:h-[68.25rem] max-lg:w-[203rem] lg:h-[76rem] lg:w-[240rem] ${
              tile === 'centre' ? '' : 'max-lg:hidden -scale-x-100'
            }`}
          >
            <Image
              src={classicalBg}
              alt=""
              loading={tile === 'centre' ? 'eager' : 'lazy'}
              className="absolute inset-0 h-full w-full max-lg:opacity-[0.1] lg:opacity-[0.2]"
            />
            <div
              className="absolute inset-0 max-lg:hidden"
              style={{
                backgroundImage:
                  'linear-gradient(180deg, rgba(255,255,255,0.31) 0%, rgba(255,255,255,0) 140%), linear-gradient(0deg, rgba(230,237,235,0.31) 0%, rgba(230,237,235,0) 14%)',
              }}
            />
            <div
              className="absolute inset-0 lg:hidden"
              style={{
                backgroundImage:
                  'linear-gradient(180deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.75) 100%)',
              }}
            />
          </div>
        ))}
      </div>

      <div className="relative z-10 flex w-full flex-col items-center gap-[0.375rem]">
        <div className="flex w-full flex-col items-center gap-[1.25rem]">
        {data.heroChip && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease }}
            className="mb-0 text-center font-blacker text-[2rem] italic leading-[1.5] text-black"
          >
            {data.heroChip}
          </motion.p>
        )}

        {data.heroTitle && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.15 }}
            className="richtext w-full text-center max-sm:max-w-[49rem] sm:max-w-[102rem] prose-headings:mb-0 prose-headings:font-medium max-lg:prose-headings:text-[5rem] max-lg:prose-headings:leading-[1.15] max-lg:prose-headings:tracking-[-0.25rem] lg:prose-headings:text-[7rem] lg:prose-headings:leading-[1.12] lg:prose-headings:tracking-[-0.25rem]"
          >
            <div className="hidden w-full lg:block">
              <PortableText value={data.heroTitle} components={richTextComponent} />
            </div>
            <div className="w-full lg:hidden">
              <PortableText
                value={data.heroTitleMobile ?? data.heroTitle}
                components={richTextComponent}
              />
            </div>
          </motion.div>
        )}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.3 }}
          className="max-lg:mt-[0.8125rem] max-lg:w-[50rem] max-lg:max-w-none lg:mt-[3.0625rem] lg:w-[65.55rem]"
        >
          <PrizeWheel onResult={setResult} spinLabel={data.spinLabel} />
        </motion.div>
      </div>

      <PrizeResultDialog
        prize={result}
        onClose={() => setResult(null)}
        copy={modalCopy}
      />
    </section>
  );
}
